import { useMemo, useState, type Dispatch, type FormEvent, type SetStateAction } from 'react';
import { Link } from 'react-router-dom';

import { IconCalendar, IconRefreshCw, IconSearch } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ExcelExportButton } from '../../components/ui/ExcelExportButton';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { formatDateTime } from '../../lib/datetime';
import { exportRowsToExcel, fetchAllPages, type ExcelColumn } from '../../lib/excel-export';
import { useTableSort } from '../../lib/table-sort';
import { useIpdDaycareLookup } from '../ipd-daycare/ipd-daycare.queries';
import { LookupSelect } from '../opd/opd.shared';
import { useOpdLookup } from '../opd/opd.queries';
import { formatOpdDateTime } from '../opd/opd.utils';
import { ReportAnalyticsGrid, ReportAreaChartCard, ReportBarChartCard, ReportDonutChartCard, type ReportChartDatum } from './ReportAnalytics';
import { reportChartColor } from './report-chart-colors';
import { currentMonthDateFilters, lastMonthDateFilters, todayDateFilters } from './report-filters';
import { fetchPatientReport } from './reports.api';
import { usePatientReport } from './reports.queries';
import type { PatientReportFilters, PatientReportModule, PatientReportRow } from './types';

interface PatientReportPageProps {
  module: PatientReportModule;
}

interface OpdPatientFilterFormProps {
  module: PatientReportModule;
  draft: PatientReportFilters;
  setDraft: Dispatch<SetStateAction<PatientReportFilters>>;
  setFilters: Dispatch<SetStateAction<PatientReportFilters>>;
  busy: boolean;
  onSubmit: (event: FormEvent) => void;
  onReset: () => void;
  onExport: () => Promise<void>;
  exportDisabled: boolean;
}

const reportTitles: Record<PatientReportModule, string> = {
  opd: 'OPD PATIENTS REPORT',
  emg: 'EMG PATIENTS REPORT',
  'ipd-daycare': 'IPD PATIENTS REPORT',
  dialysis: 'DIALYSIS PATIENTS REPORT',
  investigation: 'INVESTIGATION PATIENTS REPORT',
};

const initialFilters = (): PatientReportFilters => ({
  page: 1,
  limit: 25,
  fromDate: '',
  toDate: '',
  value: '',
  visitType: '',
  gender: '',
  status: '',
  admissionType: '',
  patientType: '',
  ward: '',
  charge: '',
  dialysisType: '',
  department: '',
  doctor: '',
  referral: '',
  marketBy: '',
  provider: '',
  hospital: '',
  opdType: '',
  sourceSection: '',
});

// Patient reports use the same registration-style layout with module-specific fields.
const registrationIdLabels: Partial<Record<PatientReportModule, string>> = {
  opd: 'OPD ID',
  emg: 'EMG ID',
  'ipd-daycare': 'IPD ID',
  dialysis: 'Dialysis ID',
  investigation: 'Investigation ID',
};

const reportDescriptions: Record<PatientReportModule, string> = {
  opd: 'OPD registrations by visit type, gender, department, consultant, and source.',
  emg: 'EMG registrations by visit type, gender, department, consultant, and source.',
  'ipd-daycare': 'IPD and daycare admissions by admission type, patient type, ward, department, doctor, and source.',
  dialysis: 'Dialysis admissions by dialysis type, patient type, ward, department, doctor, and referral.',
  investigation: 'Investigation bills by patient, source section, department, consultant, and referral source.',
};

const defaultFilters = (module: PatientReportModule): PatientReportFilters => ({
  ...initialFilters(),
  ...(module === 'opd' ? { ...lastMonthDateFilters(), hospital: 'KGH Hospital' as const }
    : registrationIdLabels[module] ? lastMonthDateFilters()
      : currentMonthDateFilters()),
});

const text = (value: string | null | undefined) => value || '-';
const upperText = (value: string | null | undefined) => text(value).toUpperCase();
const opdDoctorName = (value: string | null | undefined) => {
  const name = value?.trim().replace(/^(?:dr\.?\s*)+/i, '');
  return name && name !== '-' ? `DR. ${name}` : '';
};
const doctorReferralName = (row: PatientReportRow) => (
  `${opdDoctorName(row.doctorName) || '--'} || ${opdDoctorName(row.referralName) || '--'}`
);

export const PatientReportPage = ({ module }: PatientReportPageProps) => {
  const [draft, setDraft] = useState<PatientReportFilters>(() => defaultFilters(module));
  const [filters, setFilters] = useState<PatientReportFilters>(draft);
  const query = usePatientReport(module, filters);
  const tableSort = useTableSort(filters.sortBy ?? null, filters.sortOrder === 'desc' ? 'desc' : 'asc', (next) => {
    setFilters((current) => ({ ...current, sortBy: next.key, sortOrder: next.direction, page: 1 }));
  });
  const title = reportTitles[module];
  const idLabel = registrationIdLabels[module] ?? '';
  const isRegistration = idLabel !== '';
  const isIpd = module === 'ipd-daycare';
  const isDialysis = module === 'dialysis';
  const isInvestigation = module === 'investigation';
  const isAdmission = isIpd || isDialysis;
  const dateLabel = isInvestigation ? 'Bill Date' : isAdmission ? 'Admission Date' : 'Appointment Date';
  const typeLabel = isDialysis ? 'Type' : 'Adm. Type';
  const bedLabel = isDialysis ? 'Ward & Bed' : 'Ward / Bed';
  // Dialysis rows carry how the patient arrived (DIRECT / MOVED BY IPD...) in admissionType, shown
  // together with the visit type as in the legacy report, e.g. "DIRECT (OLD)".
  const admissionTypeText = (row: PatientReportRow) => (isDialysis ? `${text(row.admissionType)} (${text(row.visitType)})` : text(row.admissionType));

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    setFilters((current) => ({ ...draft, sortBy: current.sortBy, sortOrder: current.sortOrder, page: 1 }));
  };
  const reset = () => {
    const next = defaultFilters(module);
    setDraft(next);
    setFilters(next);
  };
  const changeLimit = (limit: number) => {
    setDraft((current) => ({ ...current, limit }));
    setFilters((current) => ({ ...current, limit, page: 1 }));
  };
  const errorMessage = query.error instanceof ApiError ? query.error.message : `${title} could not be loaded.`;

  const columnsIf = (condition: boolean, columns: ExcelColumn<PatientReportRow>[]) => (condition ? columns : []);
  const exportToExcel = async () => {
    const rows = await fetchAllPages<PatientReportRow>((page, limit) => fetchPatientReport(module, { ...filters, page, limit }), 500);
    await exportRowsToExcel({
      fileName: `${title.replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}`,
      title,
      subtitle: filters.fromDate && filters.toDate ? `${filters.fromDate} to ${filters.toDate}` : 'All dates',
      columns: isRegistration ? [
        { header: idLabel, width: 12, map: (row) => row.number },
        ...columnsIf(isAdmission, [{ header: typeLabel, width: 18, map: admissionTypeText }]),
        { header: 'Patient Name', width: 24, map: (row) => row.patientName },
        { header: 'UHID', width: 14, map: (row) => text(row.uhid) },
        { header: 'Gender', width: 12, map: (row) => text(row.gender) },
        { header: 'Age', width: 14, map: (row) => row.ageDisplay },
        { header: 'Mobile', width: 15, map: (row) => text(row.phone) },
        ...columnsIf(!isDialysis && !isInvestigation, [{ header: 'Visit Type', width: 12, map: (row) => text(row.visitType) }]),
        ...columnsIf(isInvestigation, [{ header: 'Source', width: 16, map: (row) => text(row.sourceSection) }]),
        ...columnsIf(isIpd, [{ header: 'Patient Type', width: 20, map: (row) => text(row.insuranceType) }]),
        { header: dateLabel, width: 21, map: (row) => formatOpdDateTime(row.date) },
        ...columnsIf(isAdmission, [{ header: bedLabel, width: 24, map: (row) => text(row.bed) }]),
        { header: 'Department', width: 22, map: (row) => row.department },
        { header: 'Doctor | Referred By', width: 42, map: doctorReferralName },
        ...columnsIf(isIpd, [{ header: 'Status', width: 14, map: (row) => text(row.status) }]),
      ] : [
        { header: 'No', width: 12, map: (row) => row.number },
        { header: 'Date', width: 20, map: (row) => formatDateTime(row.date) },
        { header: 'Patient', width: 24, map: patientText },
        { header: 'Gender', width: 12, map: (row) => text(row.gender) },
        { header: 'Age', width: 14, map: (row) => row.ageDisplay },
        { header: 'Visit Type', width: 12, map: (row) => text(row.visitType) },
        { header: 'Doctor', width: 22, map: (row) => row.doctorName },
        { header: 'Department', width: 18, map: (row) => row.department },
        { header: 'Status', width: 14, map: (row) => text(row.status) },
        { header: 'Patient Type', width: 18, map: (row) => text(row.insuranceType) },
        { header: 'Bed', width: 18, map: (row) => text(row.bed) },
        { header: 'Generated By', width: 18, map: (row) => row.generatedBy },
      ],
      rows,
    });
  };

  const rows = query.data?.data ?? [];
  const summary = query.data?.summary;
  const genderChartData = useMemo<ReportChartDatum[]>(() => [
    { name: 'Male', value: summary?.gender.male ?? 0, color: '#3777b7' },
    { name: 'Female', value: summary?.gender.female ?? 0, color: '#b1424d' },
    { name: 'Others', value: summary?.gender.others ?? 0, color: '#c98a1c' },
  ], [summary?.gender.female, summary?.gender.male, summary?.gender.others]);
  const visitChartData = useMemo<ReportChartDatum[]>(() => [
    { name: 'New', Count: summary?.visitType.new ?? 0 },
    { name: 'Old', Count: summary?.visitType.old ?? 0 },
  ], [summary?.visitType.new, summary?.visitType.old]);
  const departmentChartData = useMemo<ReportChartDatum[]>(() => [...(summary?.byDepartment ?? [])]
    .sort((left, right) => right.count - left.count)
    .slice(0, 8)
    .map((item, index) => ({
      name: item.name,
      Patients: item.count,
      color: reportChartColor(index),
    })), [summary?.byDepartment]);
  const opdGenderChartData = useMemo<ReportChartDatum[]>(() => {
    const genderVisitType = summary?.genderVisitType;
    const data = [
      { name: 'Male', Old: genderVisitType?.male.old ?? 0, New: genderVisitType?.male.new ?? 0 },
      { name: 'Female', Old: genderVisitType?.female.old ?? 0, New: genderVisitType?.female.new ?? 0 },
    ];
    const others = { name: 'Others', Old: genderVisitType?.others.old ?? 0, New: genderVisitType?.others.new ?? 0 };
    return others.Old + others.New > 0 ? [...data, others] : data;
  }, [summary?.genderVisitType]);
  const opdDepartmentChartData = useMemo<ReportChartDatum[]>(() => [...(summary?.byDepartment ?? [])]
    .sort((left, right) => left.name.localeCompare(right.name))
    .map((item) => ({ name: item.name, Old: item.old, New: item.new })), [summary?.byDepartment]);
  const admissionTypeChartData = useMemo<ReportChartDatum[]>(() => [
    { name: 'IPD', value: summary?.admissionType.ipd ?? 0, color: '#1689e8' },
    { name: 'DAYCARE', value: summary?.admissionType.daycare ?? 0, color: '#08a876' },
  ], [summary?.admissionType.daycare, summary?.admissionType.ipd]);
  const dialysisTypeChartData = useMemo<ReportChartDatum[]>(() => [
    { name: 'DIRECT', value: summary?.dialysisType.direct ?? 0, color: '#1689e8' },
    { name: 'MOVED BY IPD', value: summary?.dialysisType.movedByIpd ?? 0, color: '#08a876' },
    { name: 'MOVED BY EMG', value: summary?.dialysisType.movedByEmg ?? 0, color: '#c98a1c' },
  ], [summary?.dialysisType.direct, summary?.dialysisType.movedByEmg, summary?.dialysisType.movedByIpd]);
  const patientTypeChartData = useMemo<ReportChartDatum[]>(() => (summary?.byPatientType ?? [])
    .map((item) => ({ name: item.name, Patients: item.count })), [summary?.byPatientType]);
  const investigationDepartmentChartData = useMemo<ReportChartDatum[]>(() => [...(summary?.byDepartment ?? [])]
    .sort((left, right) => left.name.localeCompare(right.name))
    .map((item) => ({ name: item.name, Patients: item.count })), [summary?.byDepartment]);

  return (
    <div className="page-stack report-list-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Reports</p>
          <h1>{title}</h1>
          <p>{reportDescriptions[module]}</p>
        </div>
      </div>

      {isRegistration ? (
        <OpdPatientFilterForm
          module={module}
          draft={draft}
          setDraft={setDraft}
          setFilters={setFilters}
          busy={query.isFetching}
          onSubmit={applyFilters}
          onReset={reset}
          onExport={exportToExcel}
          exportDisabled={!query.isSuccess}
        />
      ) : (
        <form className="report-list-filter-card patient-report-filters" onSubmit={applyFilters}>
          <label>
            From date
            <input type="date" value={draft.fromDate ?? ''} max={draft.toDate || undefined} onChange={(event) => setDraft((current) => ({ ...current, fromDate: event.target.value }))} />
          </label>
          <label>
            To date
            <input type="date" value={draft.toDate ?? ''} min={draft.fromDate || undefined} onChange={(event) => setDraft((current) => ({ ...current, toDate: event.target.value }))} />
          </label>
          <label>
            Gender
            <select value={draft.gender ?? ''} onChange={(event) => setDraft((current) => ({ ...current, gender: event.target.value as PatientReportFilters['gender'] }))}>
              <option value="">All Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Others">Others</option>
            </select>
          </label>
          <label>
            Visit
            <select value={draft.visitType ?? ''} onChange={(event) => setDraft((current) => ({ ...current, visitType: event.target.value as PatientReportFilters['visitType'] }))}>
              <option value="">All Visits</option>
              <option value="new">New</option>
              <option value="old">Old</option>
            </select>
          </label>
          <label>
            Search
            <input value={draft.value ?? ''} onChange={(event) => setDraft((current) => ({ ...current, value: event.target.value }))} placeholder="Patient, UHID, phone, doctor" />
          </label>
          <button className="button button-primary report-list-action" type="submit"><IconSearch />Search</button>
          <button className="button button-secondary report-list-action" type="button" onClick={reset}><IconRefreshCw />Reset</button>
          <ExcelExportButton className="report-list-action" onExport={exportToExcel} disabled={!query.isSuccess} label="Excel" />
        </form>
      )}

      <section className="report-summary-grid">
        <SummaryTile label={isRegistration ? 'Total Patients' : 'Records'} value={summary?.count ?? 0} />
        {isIpd ? <SummaryTile label="IPD" value={summary?.admissionType.ipd ?? 0} /> : null}
        {isIpd ? <SummaryTile label="Daycare" value={summary?.admissionType.daycare ?? 0} /> : null}
        {isDialysis ? <SummaryTile label="Direct" value={summary?.dialysisType.direct ?? 0} /> : null}
        {isDialysis ? <SummaryTile label="Moved by IPD" value={summary?.dialysisType.movedByIpd ?? 0} /> : null}
        {isDialysis ? <SummaryTile label="Moved by EMG" value={summary?.dialysisType.movedByEmg ?? 0} /> : null}
        <SummaryTile label="Male" value={summary?.gender.male ?? 0} />
        <SummaryTile label="Female" value={summary?.gender.female ?? 0} />
        {isRegistration ? <SummaryTile label="Others" value={summary?.gender.others ?? 0} /> : null}
        {!isInvestigation ? <SummaryTile label="New" value={summary?.visitType.new ?? 0} /> : null}
        {!isInvestigation ? <SummaryTile label="Old" value={summary?.visitType.old ?? 0} /> : null}
      </section>

      {query.isSuccess && isIpd ? (
        <ReportAnalyticsGrid className="opd-patient-report-analytics">
          <ReportDonutChartCard eyebrow="Admission analysis" title="IPD and daycare admissions" data={admissionTypeChartData} variant="pie" height={280} />
          <ReportAreaChartCard
            eyebrow="Patient type analysis"
            title="Total Patient Types"
            data={patientTypeChartData}
            areas={[{ key: 'Patients', name: 'Patients', color: '#1689e8' }]}
            height={280}
          />
        </ReportAnalyticsGrid>
      ) : null}

      {query.isSuccess ? isRegistration ? (
        <ReportAnalyticsGrid className={isDialysis ? 'dialysis-patient-report-analytics' : 'opd-patient-report-analytics'}>
          {isDialysis ? <ReportDonutChartCard eyebrow="Dialysis analysis" title="Dialysis type" data={dialysisTypeChartData} variant="pie" height={280} /> : null}
          {isInvestigation ? (
            <>
              <ReportBarChartCard
                eyebrow="Patient analysis"
                title="Patients by gender"
                data={genderChartData}
                bars={[{ key: 'value', name: 'Patients', color: '#1689e8' }]}
                height={280}
              />
              <ReportAreaChartCard
                eyebrow="Department analysis"
                title="Total Departments"
                data={investigationDepartmentChartData}
                areas={[{ key: 'Patients', name: 'Patients', color: '#08a876' }]}
                height={280}
              />
            </>
          ) : (
            <>
              <ReportBarChartCard
                eyebrow="Patient analysis"
                title="Gender by visit type"
                data={opdGenderChartData}
                bars={[
                  { key: 'Old', name: 'Old', color: '#1689e8', stackId: 'visit-type' },
                  { key: 'New', name: 'New', color: '#08a876', stackId: 'visit-type' },
                ]}
                height={280}
              />
              <ReportAreaChartCard
                eyebrow="Department analysis"
                title="Total Departments"
                data={opdDepartmentChartData}
                areas={[
                  { key: 'Old', name: 'Old', color: '#1689e8' },
                  { key: 'New', name: 'New', color: '#08a876' },
                ]}
                height={280}
              />
            </>
          )}
        </ReportAnalyticsGrid>
      ) : (
        <ReportAnalyticsGrid>
          <ReportDonutChartCard eyebrow="Gender analysis" title="Patient gender distribution" data={genderChartData} />
          <ReportBarChartCard
            eyebrow="Visit analysis"
            title="New and old patient mix"
            data={visitChartData}
            bars={[{ key: 'Count', name: 'Patients', color: '#0d6b66' }]}
          />
          <ReportBarChartCard
            eyebrow="Department analysis"
            title="Top departments by patients"
            data={departmentChartData}
            bars={[{ key: 'Patients', name: 'Patients', color: '#3777b7' }]}
            layout="vertical"
            height={Math.max(240, departmentChartData.length * 34 + 90)}
          />
        </ReportAnalyticsGrid>
      ) : null}

      <section className="content-card report-list-card">
        <div className="table-toolbar">
          <label className="billing-limit-select">
            Show
            <select value={filters.limit} disabled={query.isFetching} onChange={(event) => changeLimit(Number(event.target.value))}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            entries
          </label>
          {isRegistration ? (
            <form className="report-table-search" onSubmit={applyFilters}>
              <label htmlFor={`${module}-report-search`}>Search:</label>
              <input
                id={`${module}-report-search`}
                value={draft.value ?? ''}
                onChange={(event) => setDraft((current) => ({ ...current, value: event.target.value }))}
                placeholder="Patient, UHID, mobile, doctor"
              />
              <button className="button button-secondary" type="submit" disabled={query.isFetching}>Search</button>
            </form>
          ) : null}
        </div>

        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState title={`Unable to load ${title}`} message={errorMessage} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess && rows.length === 0 ? <EmptyState title="No patients found" message="Change filters or date range and search again." /> : null}
        {query.isSuccess && rows.length > 0 ? (
          <>
            <div className="report-table-scroll">
              {isRegistration ? (
                <table className={`report-data-table patient-report-table opd-patient-report-table${isIpd ? ' ipd-patient-report-table' : ''}`}>
                  <thead>
                    <tr>
                      <SortableTh control={tableSort} column="number">{idLabel}</SortableTh>
                      {isAdmission ? <SortableTh control={tableSort} column="admissionType">{typeLabel}</SortableTh> : null}
                      <SortableTh control={tableSort} column="patient">Patient Name (UHID)</SortableTh>
                      <SortableTh control={tableSort} column="gender">Gender</SortableTh>
                      <th>Age</th>
                      <SortableTh control={tableSort} column="phone">Mobile</SortableTh>
                      {!isDialysis && !isInvestigation ? <SortableTh control={tableSort} column="visitType">Visit Type</SortableTh> : null}
                      {isInvestigation ? <SortableTh control={tableSort} column="sourceSection">Source</SortableTh> : null}
                      {isIpd ? <SortableTh control={tableSort} column="patientType">Patient Type</SortableTh> : null}
                      <SortableTh control={tableSort} column="date">{dateLabel}</SortableTh>
                      {isAdmission ? <SortableTh control={tableSort} column="bed">{bedLabel}</SortableTh> : null}
                      <SortableTh control={tableSort} column="department">Department</SortableTh>
                      <SortableTh control={tableSort} column="doctor">Doctor | Referred By</SortableTh>
                      {isIpd ? <SortableTh control={tableSort} column="status">Status</SortableTh> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={`${module}-${row.id}`}>
                        <td><Link className="bill-id-link" to={isInvestigation ? `/investigation-bill/${row.id}` : `/${module}/${row.id}`} title={isInvestigation ? 'Open investigation bill' : isIpd ? 'Open IPD/Daycare admission' : isDialysis ? 'Open Dialysis admission' : `Open ${module.toUpperCase()} registration`}><strong>{row.number}</strong></Link></td>
                        {isAdmission ? <td>{admissionTypeText(row).toUpperCase()}</td> : null}
                        <td>{row.patientId ? <Link className="patient-name-link" to={`/patients/${row.patientId}`} title="Open patient profile"><strong>{upperText(row.patientName)}{row.uhid ? ` (${row.uhid})` : ''}</strong></Link> : <strong>{upperText(row.patientName)}{row.uhid ? ` (${row.uhid})` : ''}</strong>}</td>
                        <td>{upperText(row.gender)}</td>
                        <td>{row.ageDisplay}</td>
                        <td>{text(row.phone)}</td>
                        {!isDialysis && !isInvestigation ? <td>{upperText(row.visitType)}</td> : null}
                        {isInvestigation ? <td>{upperText(row.sourceSection)}</td> : null}
                        {isIpd ? <td>{upperText(row.insuranceType)}</td> : null}
                        <td>{formatOpdDateTime(row.date).toUpperCase()}</td>
                        {isAdmission ? <td>{upperText(row.bed)}</td> : null}
                        <td>{upperText(row.department)}</td>
                        <td className="doctor-referral-cell">
                          <span>{(opdDoctorName(row.doctorName) || '--').toUpperCase()}</span>
                          <strong aria-hidden="true">||</strong>
                          <span>{(opdDoctorName(row.referralName) || '--').toUpperCase()}</span>
                        </td>
                        {isIpd ? <td>{upperText(row.status)}</td> : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="report-data-table patient-report-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <SortableTh control={tableSort} column="number">No</SortableTh>
                      <SortableTh control={tableSort} column="date">Date</SortableTh>
                      <SortableTh control={tableSort} column="patient">Patient</SortableTh>
                      <SortableTh control={tableSort} column="gender">Gender</SortableTh>
                      <th>Age</th>
                      <SortableTh control={tableSort} column="visitType">Visit</SortableTh>
                      <SortableTh control={tableSort} column="doctor">Doctor</SortableTh>
                      <SortableTh control={tableSort} column="department">Department</SortableTh>
                      <SortableTh control={tableSort} column="status">Status</SortableTh>
                      <th>Patient Type</th>
                      <th>Bed</th>
                      <th>Generated By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={`${row.module}-${row.id}`}>
                        <td>{(filters.page - 1) * filters.limit + index + 1}</td>
                        <td><strong>{row.number}</strong></td>
                        <td>{formatDateTime(row.date)}</td>
                        <td>{patientText(row)}</td>
                        <td>{text(row.gender)}</td>
                        <td>{row.ageDisplay}</td>
                        <td>{text(row.visitType)}</td>
                        <td>{row.doctorName}</td>
                        <td>{row.department}</td>
                        <td>{text(row.status)}</td>
                        <td>{text(row.insuranceType)}</td>
                        <td>{text(row.bed)}</td>
                        <td>{row.generatedBy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="pagination">
              <button className="button button-secondary" disabled={query.isFetching || filters.page <= 1} onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}>Previous</button>
              <span>Page {filters.page} of {Math.max(query.data.pagination.totalPages, 1)}</span>
              <button className="button button-secondary" disabled={query.isFetching || filters.page >= query.data.pagination.totalPages} onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}>Next</button>
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
};

const OpdPatientFilterForm = ({ module, draft, setDraft, setFilters, busy, onSubmit, onReset, onExport, exportDisabled }: OpdPatientFilterFormProps) => {
  const departments = useOpdLookup('departments');
  const doctors = useOpdLookup('doctors', '', draft.department ?? '');
  const referrals = useOpdLookup('referrals');
  const providers = useOpdLookup('providers');
  const marketByOptions = useOpdLookup('market-by');

  const showToday = () => {
    const dates = todayDateFilters();
    setDraft((current) => ({ ...current, ...dates }));
    setFilters((current) => ({ ...current, ...dates, page: 1 }));
  };

  return (
    <form className={`report-list-filter-card patient-report-filters opd-patient-report-filters${module === 'ipd-daycare' ? ' ipd-patient-report-filters' : ''}`} onSubmit={onSubmit}>
      {module === 'investigation' ? (
        <label>
          Source
          <select value={draft.sourceSection ?? ''} onChange={(event) => setDraft((current) => ({ ...current, sourceSection: event.target.value as PatientReportFilters['sourceSection'] }))}>
            <option value="">All Sources</option>
            <option value="INVESTIGATION">Investigation</option>
            <option value="OPD">OPD</option>
            <option value="EMG">EMG</option>
            <option value="IPD">IPD</option>
            <option value="Daycare">Daycare</option>
            <option value="Dialysis">Dialysis</option>
            <option value="OT">OT</option>
          </select>
        </label>
      ) : (
        <label>
          Visit Type
          <select value={draft.visitType ?? ''} onChange={(event) => setDraft((current) => ({ ...current, visitType: event.target.value as PatientReportFilters['visitType'] }))}>
            <option value="">All Visit Types</option>
            <option value="new">New</option>
            <option value="old">Old</option>
          </select>
        </label>
      )}
      {module === 'ipd-daycare' || module === 'dialysis' ? <AdmissionFilterFields module={module} draft={draft} setDraft={setDraft} /> : null}
      <label>
        Department
        <LookupSelect
          id={`${module}-report-department`}
          value={draft.department ?? ''}
          options={departments.data}
          placeholder={departments.isLoading ? 'Loading departments...' : 'Select Department'}
          disabled={departments.isLoading || departments.isError}
          onChange={(department) => setDraft((current) => ({ ...current, department, doctor: '' }))}
        />
      </label>
      <label>
        {module === 'opd' || module === 'emg' || module === 'investigation' ? 'Consultant Doctor' : 'Doctor'}
        <LookupSelect
          id={`${module}-report-doctor`}
          value={draft.doctor ?? ''}
          options={doctors.data}
          placeholder={doctors.isLoading ? 'Loading doctors...' : 'Select Consultant'}
          disabled={doctors.isLoading || doctors.isError}
          onChange={(doctor) => setDraft((current) => ({ ...current, doctor }))}
        />
      </label>
      <label>
        Referral
        <LookupSelect
          id={`${module}-report-referral`}
          value={draft.referral ?? ''}
          options={referrals.data}
          placeholder={referrals.isLoading ? 'Loading referrals...' : 'Select Referral'}
          disabled={referrals.isLoading || referrals.isError}
          onChange={(referral) => setDraft((current) => ({ ...current, referral }))}
        />
      </label>
      {/* Dialysis admissions do not record market-by or provider. */}
      {module !== 'dialysis' ? (
        <>
          <label>
            Market By
            <LookupSelect
              id={`${module}-report-market-by`}
              value={draft.marketBy ?? ''}
              options={marketByOptions.data}
              placeholder={marketByOptions.isLoading ? 'Loading market-by...' : 'Select Market By'}
              disabled={marketByOptions.isLoading || marketByOptions.isError}
              onChange={(marketBy) => setDraft((current) => ({ ...current, marketBy }))}
            />
          </label>
          <label>
            Provider
            <LookupSelect
              id={`${module}-report-provider`}
              value={draft.provider ?? ''}
              options={providers.data}
              placeholder={providers.isLoading ? 'Loading providers...' : 'Select Provider'}
              disabled={providers.isLoading || providers.isError}
              onChange={(provider) => setDraft((current) => ({ ...current, provider }))}
            />
          </label>
        </>
      ) : null}
      {module === 'opd' ? (
        <>
          <label>
            Hospital
            <select value={draft.hospital ?? ''} onChange={(event) => setDraft((current) => ({ ...current, hospital: event.target.value as PatientReportFilters['hospital'] }))}>
              <option value="KGH Hospital">KGH Hospital</option>
              <option value="JMN Medical College">JMN Medical College</option>
            </select>
          </label>
          <label>
            OPD Type
            <select value={draft.opdType ?? ''} onChange={(event) => setDraft((current) => ({ ...current, opdType: event.target.value as PatientReportFilters['opdType'] }))}>
              <option value="">Select OPD Type</option>
              <option value="OPD">OPD</option>
              <option value="ORC (OUT REACH PAID OPD CLINIC)">ORC (OUT REACH PAID OPD CLINIC)</option>
              <option value="Free Camp OPD">Free Camp OPD</option>
            </select>
          </label>
        </>
      ) : null}
      <label className="report-filter-wide">
        From Date
        <input type="date" value={draft.fromDate ?? ''} max={draft.toDate || undefined} onChange={(event) => setDraft((current) => ({ ...current, fromDate: event.target.value }))} />
      </label>
      <label className="report-filter-wide">
        To Date
        <input type="date" value={draft.toDate ?? ''} min={draft.fromDate || undefined} onChange={(event) => setDraft((current) => ({ ...current, toDate: event.target.value }))} />
      </label>
      <div className={`opd-patient-report-filter-actions${module === 'investigation' ? ' investigation-patient-report-filter-actions' : ''}`}>
        <button className="button button-primary report-list-action" type="submit" disabled={busy}><IconSearch />Filter</button>
        <button className="button opd-today report-list-action" type="button" onClick={showToday} disabled={busy}><IconCalendar />Today</button>
        <button className="button button-secondary report-list-action" type="button" onClick={onReset} disabled={busy}><IconRefreshCw />Reset</button>
        <ExcelExportButton className="report-list-action" onExport={onExport} disabled={exportDisabled} label="Excel" />
      </div>
    </form>
  );
};

type FilterDraftProps = Pick<OpdPatientFilterFormProps, 'draft' | 'setDraft'>;

// Admission-only filters, mounted only for IPD/Daycare and Dialysis so their lookups are not
// fetched for the other reports.
const AdmissionFilterFields = ({ module, draft, setDraft }: FilterDraftProps & { module: 'ipd-daycare' | 'dialysis' }) => {
  const patientTypes = useIpdDaycareLookup('tpas');
  const wards = useIpdDaycareLookup('wards');
  // Admissions store the patient type by name, so the filter value is the label rather than the id.
  const patientTypeOptions = patientTypes.data?.map((option) => ({ id: option.label, label: option.label }));

  return (
    <>
      {module === 'ipd-daycare' ? (
        <>
          <label>
            Admission
            <select value={draft.status ?? ''} onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value as PatientReportFilters['status'] }))}>
              <option value="">All Admissions</option>
              <option value="admitted">Admitted</option>
              <option value="discharged">Discharged</option>
            </select>
          </label>
          <label>
            Admission Type
            <select value={draft.admissionType ?? ''} onChange={(event) => setDraft((current) => ({ ...current, admissionType: event.target.value as PatientReportFilters['admissionType'] }))}>
              <option value="">All Types</option>
              <option value="IPD">IPD</option>
              <option value="Daycare">Daycare</option>
            </select>
          </label>
        </>
      ) : (
        <label>
          Dialysis Type
          <select value={draft.dialysisType ?? ''} onChange={(event) => setDraft((current) => ({ ...current, dialysisType: event.target.value as PatientReportFilters['dialysisType'] }))}>
            <option value="">All Types</option>
            <option value="DIRECT">Direct</option>
            <option value="MOVED BY IPD">Moved by IPD</option>
            <option value="MOVED BY EMG">Moved by EMG</option>
          </select>
        </label>
      )}
      <label>
        Patient Type
        <LookupSelect
          id="ipd-daycare-report-patient-type"
          value={draft.patientType ?? ''}
          options={patientTypeOptions}
          placeholder={patientTypes.isLoading ? 'Loading patient types...' : 'Select Patient Type'}
          disabled={patientTypes.isLoading || patientTypes.isError}
          onChange={(patientType) => setDraft((current) => ({ ...current, patientType }))}
        />
      </label>
      <label>
        Ward
        <LookupSelect
          id="ipd-daycare-report-ward"
          value={draft.ward ?? ''}
          options={wards.data}
          placeholder={wards.isLoading ? 'Loading wards...' : 'Select Ward'}
          disabled={wards.isLoading || wards.isError}
          onChange={(ward) => setDraft((current) => ({ ...current, ward }))}
        />
      </label>
      {module === 'ipd-daycare' ? <ChargeFilterField draft={draft} setDraft={setDraft} /> : null}
    </>
  );
};

const ChargeFilterField = ({ draft, setDraft }: FilterDraftProps) => {
  const charges = useIpdDaycareLookup('charges');
  return (
    <label className="report-filter-wide">
      Charge
      <LookupSelect
        id="ipd-daycare-report-charge"
        value={draft.charge ?? ''}
        options={charges.data}
        placeholder={charges.isLoading ? 'Loading charges...' : 'Select Charge'}
        disabled={charges.isLoading || charges.isError}
        onChange={(charge) => setDraft((current) => ({ ...current, charge }))}
      />
    </label>
  );
};

const SummaryTile = ({ label, value }: { label: string; value: number }) => (
  <div className="report-summary-tile">
    <span>{label}</span>
    <strong>{value.toLocaleString('en-IN')}</strong>
  </div>
);

const patientText = (row: Pick<PatientReportRow, 'patientName' | 'uhid' | 'phone'>) =>
  [row.patientName, row.uhid ? `UHID ${row.uhid}` : null, row.phone].filter(Boolean).join(' | ');
